import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AuthContext } from "../../../context/Auth/AuthContext";
import getOcrFullData from "../../../api/getOcrFullData";
import updateOcrFullData from "../../../api/updateOcrFullData";
import { showSuccess, showError } from "../../../utils/toast";

// Side panel for the full-document OCR JSON (ocr_data.ocr_full_data):
// page filter chips -> page cards -> section cards -> label/value rows (+ tables).
// Values are selectable text with Copy actions. "Edit" lets the user correct
// values or remove fields; Save writes only ocr_full_data (ocr_data and the
// document are untouched, and Re-OCR still overwrites it).

const copyText = (text, what) => {
  if (!navigator.clipboard) {
    showError("Copy is not available in this browser");
    return;
  }
  navigator.clipboard.writeText(text).then(
    () => showSuccess(`${what} copied`),
    () => showError("Could not copy to clipboard")
  );
};

const sectionToText = (section) =>
  [section.name, ...section.fields.map((f) => `${f.label}: ${f.value}`)].join("\n");

// Tables are shown in the same label/value form as other sections (no
// horizontal scrolling): each table row becomes a section whose labels are
// the column headers. Display only - the stored JSON keeps its tables.
// Every field carries a key pointing back to where it is stored
// ("page|s|section|field" or "page|t|table|row|column") so edits and
// deletions can be written back to the same place.
const tableToSections = (table, pageNo, ti) => {
  const columns = table.columns || [];
  const rows = (table.rows || [])
    .map((row, ri) => ({ row, ri }))
    .filter(({ row }) => row && row.some((cell) => String(cell ?? "").trim() !== ""));
  const baseName = table.name || "Details";
  return rows
    .map(({ row, ri }, n) => {
      const width = Math.max(columns.length, row.length);
      const fields = Array.from({ length: width }, (_, ci) => ({
        label: columns[ci] || `Column ${ci + 1}`,
        value: String(row[ci] ?? ""),
        key: `${pageNo}|t|${ti}|${ri}|${ci}`,
      })).filter((field) => field.value.trim() !== "");
      return { name: rows.length > 1 ? `${baseName} (${n + 1})` : baseName, fields };
    })
    .filter((section) => section.fields.length > 0);
};

const pageSections = (page) => [
  ...(page.sections || []).map((section, si) => ({
    ...section,
    fields: (section.fields || []).map((field, fi) => ({ ...field, key: `${page.page}|s|${si}|${fi}` })),
  })),
  ...(page.tables || []).flatMap((table, ti) => tableToSections(table, page.page, ti)),
];

// Stored pages with the user's edits applied: edited values replaced, deleted
// section fields removed, deleted table cells emptied (keeps columns aligned).
// Page -> section -> field structure is otherwise unchanged.
// A whole page removed with "Delete page" is keyed "page|page".
const pageKey = (pageNo) => `${pageNo}|page`;

const applyChanges = (pages, edits, deleted) =>
  pages.filter((page) => !deleted[pageKey(page.page)]).map((page) => {
    const sections = (page.sections || []).map((section, si) => ({
      ...section,
      fields: (section.fields || [])
        .map((field, fi) => {
          const key = `${page.page}|s|${si}|${fi}`;
          if (deleted[key]) return null;
          return key in edits ? { ...field, value: edits[key] } : field;
        })
        .filter(Boolean),
    }));
    const tables = (page.tables || []).map((table, ti) => ({
      ...table,
      rows: (table.rows || []).map((row, ri) =>
        (row || []).map((cell, ci) => {
          const key = `${page.page}|t|${ti}|${ri}|${ci}`;
          if (deleted[key]) return "";
          return key in edits ? edits[key] : cell;
        })
      ),
    }));
    return { ...page, sections, tables };
  });

const pageToText = (page) =>
  [
    `Page ${page.page} — ${page.category}${page.title ? ` — ${page.title}` : ""}`,
    page.summary || null,
    ...pageSections(page).map(sectionToText),
  ]
    .filter(Boolean)
    .join("\n\n");

const fieldCount = (page, deleted = {}) =>
  pageSections(page).reduce((sum, sec) => sum + sec.fields.filter((f) => !deleted[f.key]).length, 0);

// Page icon colours rotate like the reference (blue, green, purple, orange).
const PAGE_TONES = [
  "text-blue-600",
  "text-emerald-600",
  "text-violet-600",
  "text-orange-500",
];

// Section icon + tint chosen from the section name.
const sectionStyle = (name) => {
  const n = String(name || "").toLowerCase();
  if (/client|personal|applicant|holder|name|contact|insured|customer/.test(n))
    return { Icon: PersonOutlineOutlinedIcon, header: "bg-[#eef8f1]", icon: "text-emerald-600" };
  if (/address|location|residence/.test(n))
    return { Icon: PlaceOutlinedIcon, header: "bg-[#eef8f1]", icon: "text-emerald-600" };
  if (/payment|amount|premium|billing|financial|balance|fee|charge/.test(n))
    return { Icon: PaymentsOutlinedIcon, header: "bg-[#fff6ec]", icon: "text-orange-500" };
  if (/date|period|term|schedule/.test(n))
    return { Icon: EventOutlinedIcon, header: "bg-[#eef4ff]", icon: "text-blue-600" };
  if (/document|policy|contract|agreement|form|notice|certificate|identity|id\b/.test(n))
    return { Icon: ArticleOutlinedIcon, header: "bg-[#f3f0ff]", icon: "text-violet-600" };
  return { Icon: InfoOutlinedIcon, header: "bg-[#eef6f8]", icon: "text-[#1f6f7f]" };
};

// Small grey icon action (copy / edit / delete) used across the panel.
const IconAction = ({ title, onClick, icon: Icon = ContentCopyOutlinedIcon, danger = false, className = "" }) => (
  <Tooltip title={title} arrow>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={title}
      className={`inline-flex shrink-0 items-center justify-center rounded p-1 text-gray-400 hover:bg-white ${
        danger ? "hover:text-red-600" : "hover:text-[#1f6f7f]"
      } ${className}`}
    >
      <Icon sx={{ fontSize: 14 }} />
    </button>
  </Tooltip>
);

const CopyButton = (props) => <IconAction {...props} />;

const OcrFullDataDialog = ({ open, onClose, documentID }) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [pageFilter, setPageFilter] = useState("all");
  const [openPages, setOpenPages] = useState({});
  const [closedSections, setClosedSections] = useState({});
  // Edit mode: pending value edits and deleted fields, keyed by field key.
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [deleted, setDeleted] = useState({});
  const [saving, setSaving] = useState(false);

  const exitEdit = () => {
    setEditing(false);
    setEdits({});
    setDeleted({});
  };

  const load = useCallback(async () => {
    if (!documentID) return;
    setEditing(false);
    setEdits({});
    setDeleted({});
    setLoading(true);
    const res = await getOcrFullData({ token, documentId: documentID });
    const next = res?.code === 200 ? res.data : null;
    setData(next);
    setPageFilter("all");
    // First page open by default, the rest collapsed (as in the reference).
    setOpenPages(next?.pages?.length ? { [next.pages[0].page]: true } : {});
    setClosedSections({});
    setLoading(false);
  }, [documentID, token]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const pages = useMemo(() => data?.pages || [], [data]);
  // Pages marked for deletion are hidden until Save (or restored by Cancel).
  const livePages = pages.filter((p) => !deleted[pageKey(p.page)]);
  const visiblePages =
    pageFilter === "all" ? livePages : livePages.filter((p) => p.page === pageFilter);

  const [confirmPage, setConfirmPage] = useState(null);

  // Page-level edit: switch to edit mode with that page expanded.
  const editPage = (n) => {
    setEditing(true);
    setOpenPages((o) => ({ ...o, [n]: true }));
  };

  // Confirmed page delete: removed from the pending changes; Save stores it.
  const deletePage = (n) => {
    setEditing(true);
    setDeleted((d) => ({ ...d, [pageKey(n)]: true }));
    if (pageFilter === n) setPageFilter("all");
    setConfirmPage(null);
  };

  const togglePage = (n) => setOpenPages((o) => ({ ...o, [n]: !o[n] }));
  const toggleSection = (key) => setClosedSections((c) => ({ ...c, [key]: !c[key] }));

  const hasChanges = Object.keys(edits).length > 0 || Object.keys(deleted).length > 0;

  const save = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    const res = await updateOcrFullData({
      token,
      documentId: documentID,
      pages: applyChanges(pages, edits, deleted),
    });
    setSaving(false);
    if (res?.status === "success" && res.data) {
      setData(res.data);
      exitEdit();
      showSuccess("Extracted data saved");
    } else {
      showError(res?.error || "Could not save the extracted data");
    }
  };

  const chipClass = (active) =>
    `shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
      active
        ? "border-blue-600 bg-blue-600 text-white"
        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
    }`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, maxWidth: "100%" } }}
    >
      <div className="flex h-full flex-col bg-white">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 px-4 pb-2 pt-3">
          <div className="flex min-w-0 items-start gap-2">
            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} className="mt-0.5 text-blue-600" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-tight text-gray-900">Extracted Document Data</h2>
              <p className="text-xs text-gray-500">
                {editing
                  ? "Correct values or remove fields, then Save."
                  : "Data extracted from the complete document. View the information below."}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {editing ? (
              <div className="mr-1 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={exitEdit}
                  disabled={saving}
                  className="h-7 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={!hasChanges || saving}
                  className="h-7 rounded-md bg-[#1a6b7c] px-3 text-xs font-semibold text-white hover:bg-[#155e6e] disabled:cursor-not-allowed disabled:bg-[#1a6b7c]/40"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            ) : (
              <>
                {pages.length > 0 && (
                  <Tooltip title="Copy full JSON" arrow>
                    <IconButton size="small" onClick={() => copyText(JSON.stringify(data, null, 2), "Full JSON")}>
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {pages.length > 0 && (
                  <Tooltip title="Edit" arrow>
                    <IconButton size="small" onClick={() => setEditing(true)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Refresh" arrow>
                  <span>
                    <IconButton size="small" onClick={load} disabled={loading}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Tooltip title="Close" arrow>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Page filter chips */}
        {livePages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-2">
            <button type="button" className={chipClass(pageFilter === "all")} onClick={() => setPageFilter("all")}>
              All Pages ({livePages.length})
            </button>
            {livePages.map((p) => (
              <button
                key={p.page}
                type="button"
                className={chipClass(pageFilter === p.page)}
                onClick={() => {
                  setPageFilter(p.page);
                  setOpenPages((o) => ({ ...o, [p.page]: true }));
                }}
              >
                Page {p.page}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="min-h-0 flex-1 select-text overflow-y-auto border-t border-gray-100 px-4 py-3">
          {loading ? (
            <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
          ) : !data ? (
            <div className="py-10 text-center">
              <p className="text-sm text-gray-600">Extracted document data is not available yet.</p>
              <p className="mt-1 text-xs text-gray-400">
                It is generated in the background after Perform OCR / Re-OCR. Longer PDFs can take a
                few minutes — use Refresh to check again.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {data.failed_pages?.length > 0 && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700">
                  Not extracted: page {data.failed_pages.join(", ")}
                </p>
              )}

              {livePages.length === 0 && (
                <p className="py-6 text-center text-xs text-gray-500">
                  All pages removed. Save to apply, or Cancel to restore them.
                </p>
              )}

              {visiblePages.map((page) => {
                const isOpen = pageFilter !== "all" || !!openPages[page.page];
                const tone = PAGE_TONES[(page.page - 1) % PAGE_TONES.length];
                const count = fieldCount(page, deleted);
                return (
                  <section key={page.page} className="rounded-lg border border-gray-200 bg-[#f7f9fb]">
                    {/* Page header */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => togglePage(page.page)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          togglePage(page.page);
                        }
                      }}
                      aria-expanded={isOpen}
                      className="flex cursor-pointer items-center gap-2.5 px-3 py-2"
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: 22 }} className={tone} />
                      <span className="text-sm font-semibold text-gray-900">Page {page.page}</span>
                      <span className="rounded-full bg-[#e8f0fb] px-2 py-0.5 text-[11px] text-[#35609b]">
                        {count} field{count === 1 ? "" : "s"}
                      </span>
                      {page.category && (
                        <span className="hidden min-w-0 truncate text-[11px] text-gray-500 sm:inline" title={page.category}>
                          {page.category}
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-0.5">
                        <CopyButton title={`Copy page ${page.page}`} onClick={() => copyText(pageToText(page), `Page ${page.page}`)} />
                        {!editing && (
                          <IconAction icon={EditOutlinedIcon} title={`Edit page ${page.page}`} onClick={() => editPage(page.page)} />
                        )}
                        <IconAction
                          icon={DeleteOutlineIcon}
                          danger
                          title={`Delete page ${page.page}`}
                          onClick={() => setConfirmPage(page.page)}
                        />
                        {pageFilter === "all" &&
                          (isOpen ? (
                            <ExpandLessIcon sx={{ fontSize: 20 }} className="text-gray-600" />
                          ) : (
                            <ChevronRightIcon sx={{ fontSize: 20 }} className="text-gray-600" />
                          ))}
                      </span>
                    </div>

                    {isOpen && (
                      <div className="flex flex-col gap-2 px-2 pb-2">
                        {page.error && (
                          <p className="px-1 text-xs text-amber-700">{page.error}</p>
                        )}
                        {page.summary && <p className="px-1 text-xs italic text-gray-500">{page.summary}</p>}
                        {pageSections(page).length === 0 && !page.error && (
                          <p className="px-1 text-xs text-gray-400">No data extracted from this page.</p>
                        )}

                        {pageSections(page).map((section, si) => {
                          const key = `${page.page}-${si}`;
                          const closed = !!closedSections[key];
                          const { Icon, header, icon } = sectionStyle(section.name);
                          return (
                            <div key={key} className="overflow-hidden rounded-md border border-gray-200 bg-white">
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleSection(key)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleSection(key);
                                  }
                                }}
                                aria-expanded={!closed}
                                className={`flex cursor-pointer items-center gap-2 px-3 py-2 ${header}`}
                              >
                                <Icon sx={{ fontSize: 18 }} className={icon} />
                                <span className="min-w-0 truncate text-xs font-semibold text-gray-800">{section.name}</span>
                                <span className="ml-auto flex items-center gap-0.5">
                                  <CopyButton
                                    title={`Copy ${section.name}`}
                                    onClick={() => copyText(sectionToText(section), section.name)}
                                  />
                                  {closed ? (
                                    <ExpandMoreIcon sx={{ fontSize: 18 }} className="text-gray-600" />
                                  ) : (
                                    <ExpandLessIcon sx={{ fontSize: 18 }} className="text-gray-600" />
                                  )}
                                </span>
                              </div>
                              {!closed && (
                                <dl className="divide-y divide-gray-100">
                                  {section.fields.filter((field) => !deleted[field.key]).length === 0 && (
                                    <p className="px-3 py-1.5 text-xs text-gray-400">All fields removed.</p>
                                  )}
                                  {section.fields.map((field) => {
                                    if (deleted[field.key]) return null;
                                    const value = field.key in edits ? edits[field.key] : field.value;
                                    return (
                                    <div key={field.key} className="group grid grid-cols-[minmax(0,42%)_1fr]">
                                      <dt className="border-r border-gray-100 px-3 py-1.5 text-xs text-gray-600 [overflow-wrap:anywhere]">
                                        {field.label}
                                      </dt>
                                      {editing ? (
                                        <dd className="flex items-start gap-1 px-2 py-1">
                                          <textarea
                                            value={value}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setEdits((prev) => {
                                                const next = { ...prev };
                                                if (v === field.value) delete next[field.key];
                                                else next[field.key] = v;
                                                return next;
                                              });
                                            }}
                                            rows={Math.min(
                                              4,
                                              Math.max(1, value.split("\n").length, Math.ceil(value.length / 36))
                                            )}
                                            aria-label={`${field.label} value`}
                                            className={`min-w-0 flex-1 resize-none rounded-md border px-2 py-0.5 text-xs leading-5 text-gray-900 transition-colors focus:border-[#1a6b7c] focus:bg-white focus:outline-none ${
                                              field.key in edits
                                                ? "border-amber-200 bg-[#fffaf0]"
                                                : "border-transparent bg-[#f4f7f9] hover:border-gray-200"
                                            }`}
                                          />
                                          <IconAction
                                            icon={DeleteOutlineIcon}
                                            danger
                                            title="Delete field"
                                            onClick={() => {
                                              setDeleted((d) => ({ ...d, [field.key]: true }));
                                              setEdits((prev) => {
                                                const next = { ...prev };
                                                delete next[field.key];
                                                return next;
                                              });
                                            }}
                                            className="mt-0.5 opacity-50 focus:opacity-100 group-hover:opacity-100"
                                          />
                                        </dd>
                                      ) : (
                                      <dd className="flex items-start justify-between gap-1 px-3 py-1.5 text-xs text-gray-900">
                                        <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]">
                                          {field.value || <span className="text-gray-300">—</span>}
                                        </span>
                                        {field.value && (
                                          <CopyButton
                                            title="Copy value"
                                            onClick={() => copyText(field.value, field.label || "Value")}
                                            className="-my-0.5 opacity-0 focus:opacity-100 group-hover:opacity-100"
                                          />
                                        )}
                                      </dd>
                                      )}
                                    </div>
                                    );
                                  })}
                                </dl>
                              )}
                            </div>
                          );
                        })}

                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Confirm before removing a whole page */}
      <Dialog open={confirmPage !== null} onClose={() => setConfirmPage(null)} maxWidth="xs" fullWidth>
        <div className="px-4 pb-3 pt-4">
          <h3 className="text-sm font-semibold text-gray-900">Delete page {confirmPage}?</h3>
          <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
            All extracted data for page {confirmPage} will be removed from the extracted document data when you click
            Save. The document itself and the OCR fields are not affected. Cancel keeps the page.
          </p>
          <div className="mt-4 flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setConfirmPage(null)}
              className="h-7 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => deletePage(confirmPage)}
              className="h-7 rounded-md bg-red-600 px-3 text-xs font-semibold text-white hover:bg-red-700"
            >
              Delete page
            </button>
          </div>
        </div>
      </Dialog>
    </Drawer>
  );
};

export default OcrFullDataDialog;
